import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

interface DiffLeaf {
  _leaf: true;
  _modified: true;
  _type: 'created' | 'modified' | 'deleted';
  oldValue: unknown;
  oldValueType: string;
  newValue: unknown;
  newValueType: string;
}

interface DiffData {
  [field: string]: DiffLeaf;
}

interface AuditDiffTableProps {
  diff: DiffData | null | undefined;
}

const TYPE_COLORS: Record<string, string> = {
  created: '#1d8102',
  modified: '#ffe457',
  deleted: '#d13212',
};

function toText(value: unknown, type: string): string | null {
  if (value === null || value === undefined) return null;
  if (type === 'boolean') return String(value);
  if (type === 'number') return String(value);
  if (type === 'object') return JSON.stringify(value);
  if (type === 'date') return new Date(value as string).toLocaleString();
  if (type === 'string') return value as string;
  return String(value);
}

function formatValue(value: unknown, type: string): React.ReactNode {
  const text = toText(value, type);
  if (text === null) return <em style={{ color: '#999' }}>null</em>;
  if (type === 'string') return `"${text}"`;
  return text;
}

interface Segment {
  text: string;
  type: 'equal' | 'removed' | 'added';
}

interface StringDiff {
  oldSegments: Segment[];
  newSegments: Segment[];
}

function diffStrings(oldStr: string, newStr: string): StringDiff {
  const oldLen = oldStr.length;
  const newLen = newStr.length;

  if (oldLen + newLen > 4000) {
    return {
      oldSegments: [{ text: oldStr, type: 'removed' }],
      newSegments: [{ text: newStr, type: 'added' }],
    };
  }

  const dp: Uint16Array[] = Array.from({ length: oldLen + 1 }, () => new Uint16Array(newLen + 1));
  for (let i = 1; i <= oldLen; i++) {
    for (let j = 1; j <= newLen; j++) {
      dp[i][j] =
        oldStr[i - 1] === newStr[j - 1]
          ? dp[i - 1][j - 1] + 1
          : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }

  let i = oldLen;
  let j = newLen;
  const revOld: Segment[] = [];
  const revNew: Segment[] = [];
  let revEq: string[] = [];

  while (i > 0 && j > 0) {
    if (oldStr[i - 1] === newStr[j - 1]) {
      revEq.push(oldStr[i - 1]);
      i--;
      j--;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      if (revEq.length) {
        const eq = revEq.reverse().join('');
        revOld.push({ text: eq, type: 'equal' });
        revNew.push({ text: eq, type: 'equal' });
        revEq = [];
      }
      revOld.push({ text: oldStr[i - 1], type: 'removed' });
      i--;
    } else {
      if (revEq.length) {
        const eq = revEq.reverse().join('');
        revOld.push({ text: eq, type: 'equal' });
        revNew.push({ text: eq, type: 'equal' });
        revEq = [];
      }
      revNew.push({ text: newStr[j - 1], type: 'added' });
      j--;
    }
  }
  if (revEq.length) {
    const eq = revEq.reverse().join('');
    revOld.push({ text: eq, type: 'equal' });
    revNew.push({ text: eq, type: 'equal' });
  }
  while (i > 0) {
    revOld.push({ text: oldStr[i - 1], type: 'removed' });
    i--;
  }
  while (j > 0) {
    revNew.push({ text: newStr[j - 1], type: 'added' });
    j--;
  }

  const merge = (segs: Segment[]): Segment[] => {
    const result: Segment[] = [];
    for (let k = segs.length - 1; k >= 0; k--) {
      const s = segs[k];
      if (result.length && result[result.length - 1].type === s.type) {
        result[result.length - 1].text += s.text;
      } else {
        result.push({ ...s });
      }
    }
    return result;
  };

  return { oldSegments: merge(revOld), newSegments: merge(revNew) };
}

const HIGHLIGHT: Record<string, React.CSSProperties> = {
  removed: {
    backgroundColor: 'rgba(209, 50, 18, 0.18)',
    textDecoration: 'line-through',
    borderRadius: 2,
    padding: '0 1px',
  },
  added: {
    backgroundColor: 'rgba(29, 129, 2, 0.18)',
    fontWeight: 600,
    borderRadius: 2,
    padding: '0 1px',
  },
};

function renderSegments(segments: Segment[]): React.ReactNode {
  return segments.map((seg, idx) =>
    seg.type === 'equal' ? (
      <span key={idx}>{seg.text}</span>
    ) : (
      <span key={idx} style={HIGHLIGHT[seg.type]}>
        {seg.text}
      </span>
    )
  );
}

interface DiffCellProps {
  value: unknown;
  type: string;
  segments?: Segment[] | null;
}

function DiffCell({ value, type, segments }: DiffCellProps): React.ReactElement {
  if (value === null || value === undefined) {
    return <em style={{ color: '#999' }}>null</em>;
  }
  if (segments) {
    const quote = type === 'string' ? '"' : '';
    return (
      <span>
        {quote}
        {renderSegments(segments)}
        {quote}
      </span>
    );
  }
  return <span>{formatValue(value, type)}</span>;
}

const AuditDiffTable: React.FC<AuditDiffTableProps> = ({ diff }) => {
  if (!diff || typeof diff !== 'object') {
    return (
      <Typography variant="body2" color="text.secondary">
        No changes recorded.
      </Typography>
    );
  }

  const entries = Object.entries(diff).filter(([, v]) => v && v._leaf && v._modified);

  if (entries.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No field changes detected.
      </Typography>
    );
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '10%' }} />
            <TableCell sx={{ width: '25%' }}>
              <strong>Field</strong>
            </TableCell>
            <TableCell sx={{ width: '32%' }}>
              <strong>Before</strong>
            </TableCell>
            <TableCell sx={{ width: '33%' }}>
              <strong>After</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map(([field, change]) => {
            let oldSegments: Segment[] | null = null;
            let newSegments: Segment[] | null = null;
            if (change._type === 'modified') {
              const oldText = toText(change.oldValue, change.oldValueType);
              const newText = toText(change.newValue, change.newValueType);
              if (oldText !== null && newText !== null) {
                const d = diffStrings(oldText, newText);
                oldSegments = d.oldSegments;
                newSegments = d.newSegments;
              }
            }

            const rowBg =
              change._type === 'created'
                ? 'rgba(29, 129, 2, 0.08)'
                : change._type === 'deleted'
                  ? 'rgba(209, 50, 18, 0.08)'
                  : 'rgba(255, 228, 87, 0.15)';

            return (
              <TableRow key={field} sx={{ backgroundColor: rowBg }}>
                <TableCell>
                  <span
                    style={{
                      borderRadius: 3,
                      padding: '1px 5px',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      color: change._type === 'modified' ? '#000' : '#fff',
                      backgroundColor: TYPE_COLORS[change._type] || '#666',
                      display: 'inline-block',
                    }}
                  >
                    {change._type?.toUpperCase()}
                  </span>
                </TableCell>
                <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#008f7e' }}>
                  {field}
                </TableCell>
                <TableCell
                  sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}
                >
                  {change._type === 'created' ? (
                    <em style={{ color: '#999' }}>—</em>
                  ) : (
                    <DiffCell
                      value={change.oldValue}
                      type={change.oldValueType}
                      segments={oldSegments}
                    />
                  )}
                </TableCell>
                <TableCell
                  sx={{ fontFamily: 'monospace', fontSize: '0.8rem', wordBreak: 'break-all' }}
                >
                  {change._type === 'deleted' ? (
                    <em style={{ color: '#999' }}>—</em>
                  ) : (
                    <DiffCell
                      value={change.newValue}
                      type={change.newValueType}
                      segments={newSegments}
                    />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default AuditDiffTable;
export type { DiffData, DiffLeaf };
